import {compareVersions} from "./version";

/**
 * Represents a message which we want to send to the mobile apps. It consists of an 'action' string and a 'data' object.
 *
 * The 'action' string indicates what action we want the mobile apps to perform OR what action the Web App has
 * performed which we want to notify the mobile apps about.
 *
 * The 'data' object contains extra information which the mobile apps need to perform the action. An example would be
 * a phone number so the mobile apps can send a text message invite.
 * Here's an example data object:
 * {
 *   phoneNumber: "(801) 555-555",
 *   message: "Join me on Cook'n!"
 * }
 */
export class Message {
    static ACTION_GET_CONTACT_LIST = "get-contact-list"
    static ACTION_SEND_TEXT_MESSAGE = "send-text-message"
    static ACTION_DID_READ_CHAT_CONVERSATION = "did-read-chat-conversation"
    static ACTION_DID_SEND_INVITE = "did-send-invite"
    static ACTION_GET_FACEBOOK_ACCESS_TOKEN = "get-facebook-access-token"
    static ACTION_DO_FACEBOOK_LOGIN = "do-facebook-login"
    static ACTION_GET_FACEBOOK_PERMISSIONS = "get-facebook-permissions"
    static ACTION_DO_FACEBOOK_LOGOUT = "do-facebook-logout"
    static ACTION_DO_FACEBOOK_INVITE_POST = "do-facebook-invite-post"
    static ACTION_DID_OPEN_FACEBOOK_SHARE_DIALOG = "did-open-facebook-share-dialog"

    constructor(action, data) {
        this.action = action
        this.data = data
    }

    toJson() {
        return {
            "action": this.action,
            "data": this.data
        }
    }

    toJsonStr() {
        return JSON.stringify(this.toJson())
    }
}

/**
 * Sends a Message to mobile only if the specified device is a mobile device and the specified version indicates that
 * the device can receive the message.
 *
 * @param device A String which identifies the platform. ("ios", "win", "and", etc.)
 * @param version A String which identifies the version of the platform. ("5.0.7", "25.6.0", etc.)
 * @param message The Message instance we wish to send.
 */
export function postMessageToMobile(device, version, message) {
    if (!isMobile(device)) {
        console.log("clientCommunication.js postMessageToMobile: device [" + device + "] is not mobile, can't post message")
        return
    }
    if (!canPostMessageToMobile(device, version)) {
        console.log("clientCommunication.js postMessageToMobile: version [" + version + "] is too low, can't post message")
        return
    }

    if (isIOS(device)) {
        postMessageToIOS(message)
    } else if (isAndroid(device)) {
        postMessageToAndroid(message)
    } else {
        // This is an error
        console.log("clientCommunication.js postMessageToMobile: Error posting message. device=[" + device + "], " +
            "version=[" + version + "], message=[" + message.toJson() + "]")
    }
}

/**
 * Determines if the device is a mobile device.
 *
 * @param device A String which identifies the platform. ("ios", "and", "win", etc.)
 *
 * @returns {boolean} true if the device is a mobile device, false if not.
 */
export function isMobile(device) {
    return isIOS(device) || isAndroid(device)
}

/**
 * Determines if we can post a Message to the current device.
 *
 * @param device A String which identifies the platform. ("ios", "and", "win", etc.)
 * @param version A String which identifies the version of the platform. ("5.0.7", "25.6.0", etc.)
 *
 * @returns {boolean} true if we can post the message to mobile, false if not.
 */
export function canPostMessageToMobile(device, version) {
    return (isIOS(device) && canPostMessageToIOS(version))
        || (isAndroid(device) && canPostMessageToAndroid(version))
}


//// iOS

/**
 * Posts a Message to the iOS app via WKScriptMessageHandler.
 * See https://diamantidis.github.io/2020/02/02/two-way-communication-between-ios-wkwebview-and-web-page.
 *
 * The iOS app will receive the message in MyFriendsViewController#userContentController:didReceiveScriptMessage:
 *
 * @param message The Message instance we wish to send.
 */
export function postMessageToIOS(message) {
    const messageJson = message.toJson()
    console.log("clientCommunication.js postMessageToIOS: message=[" + messageJson + "]")
    if (window.webkit
        && window.webkit.messageHandlers
        && window.webkit.messageHandlers.ComDvoCooknAppMobileIosMessageHandler)
    {
        window.webkit.messageHandlers.ComDvoCooknAppMobileIosMessageHandler.postMessage(messageJson)
        console.log("clientCommunication.js postMessageToIOS: Message posted successfully")
        return true
    }
    console.log("clientCommunication.js postMessageToIOS: 'ComDvoCooknAppMobileIosMessageHandler' not found, message not posted")
    return false
}

/**
 * Determines if we can send a Message to iOS.
 *
 * @param version A String which identifies the version of the platform. ("5.0.7", "25.6.0", etc.)
 *
 * @returns {boolean} true if we can send the Message, false if not.
 */
export function canPostMessageToIOS(version) {
    return (version && compareVersions(version, "25.6.0") >= 0)
}

/**
 * Determines if the device is iOS.
 *
 * @param device A String which identifies the platform. ("ios", "and", "win", etc.)
 *
 * @returns {boolean} true if the device is iOS, false if not.
 */
export function isIOS(device) {
    if (!device) {
        return false
    }
    return device === "ios"
}


//// Android

/**
 * Posts a message to the Android app via JavaScriptInterface.
 * See https://stackoverflow.com/questions/52617884/reactjs-communicate-with-webview-in-native-android-android-is-not-defined-no.
 *
 * The Android app will receive the message in WebAppInterface#webAppDidSendMessage
 *
 * @param message The Message instance we wish to send.
 */
export function postMessageToAndroid(message) {
    console.log("clientCommunication.js postMessageToAndroid: message=[" + message.toJson() + "]")
    if (window.ComDvoCooknAppMobileAndroid) {
        window.ComDvoCooknAppMobileAndroid.webAppDidSendMessage(message.toJsonStr())
        console.log("clientCommunication.js postMessageToAndroid: Message posted successfully")
        return true;
    }
    console.log("clientCommunication.js postMessageToAndroid: 'ComDvoCooknAppMobileAndroid' not found, message not posted")
    return false
}

/**
 * Determines if we can send a Message to Android.
 *
 * @param version A String which identifies the version of the platform. ("5.0.7", "25.6.0", etc.)
 *
 * @returns {boolean} true if we can send the Message, false if not.
 */
export function canPostMessageToAndroid(version) {
    return (version && compareVersions(version, "5.0.7") >= 0)
}

/**
 * Determines if the device is Android.
 *
 * @param device A String which identifies the platform. ("ios", "and", "win", etc.)
 *
 * @returns {boolean} true if the device is Android, false if not.
 */
export function isAndroid(device) {
    if (!device) {
        return false
    }
    return device === "and" || device === "google_and" || device === "amazon_and"
}


//// Windows

/**
 * Posts a message to the Windows app via eclipse BrowserFunction.
 *
 * @param message The Message instance to send.
 */
export function postMessageToWindows(message) {
    // if (typeof window.messageWindows === "function") {
    if (window.messageWindows) {
        window.messageWindows(message.toJsonStr())
        console.log("clientCommunication.js postMessageToWindows: Message posted successfully")
        return true
    }
    console.log("clientCommunication.js postMessageToWindows: 'messageWindows' function not found, message not posted")
    return false
}

/**
 * Determines if the device is Windows.
 *
 * @param device A String which identifies the platform. ("ios", "and", "win", etc.)
 *
 * @returns {boolean} true if the device is Windows, false if not.
 */
export function isWindows(device) {
    if (!device) {
        return false
    }
    return device === "win"
}



// WEBPACK FOOTER //
// ./src/Helpers/clientCommunication.js